import { useRef } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@adora/shadcn-ui";
import { BaseDialogProps } from "../../contexts/dialog-context";
import { Z_CONFIRMATION_DIALOG } from "../../constants/z-index";

interface ConfirmationDialogProps extends BaseDialogProps {
  title: string;
  description: string;
  primaryButtonText: string;
  danger?: true;
  showCancel?: boolean;
}

/** Asks to confirm an action: the primary button resolves `true`; Cancel, Escape or a click outside, `false`. */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  title,
  description,
  primaryButtonText,
  danger = false,
  showCancel = true
}) => {
  const primaryButtonRef = useRef<HTMLButtonElement>(null);
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose(false)}>
      <DialogContent
        size="sm"
        overlayZIndex={Z_CONFIRMATION_DIALOG}
        style={{ zIndex: Z_CONFIRMATION_DIALOG }}
        // Existing analytics track this action name, the old dialog's; keep it verbatim.
        data-action-name="confirm dialog submit dialog"
        // The primary action is focused on open, so Enter confirms.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          primaryButtonRef.current?.focus();
        }}
      >
        <DialogHeader>
          {/* Smaller than the stock title and never clamped: some confirmation titles run past two lines at the
              stock size in this narrow dialog. */}
          <DialogTitle className="text-3xl leading-9 line-clamp-none">{title}</DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="pt-2">
          {showCancel && (
            <Button
              variant="outline"
              onClick={() => onClose(false)}
              // Existing analytics track these action names; keep them verbatim.
              data-action-name="confirm dialog submit dialog cancel button button"
            >
              Cancel
            </Button>
          )}
          <Button
            variant={danger ? "destructive" : "default"}
            onClick={() => onClose(true)}
            ref={primaryButtonRef}
            data-action-name="confirm dialog submit dialog submit button button"
          >
            {primaryButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
